import { useMarker } from '@/hooks/map';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore } from '@/stores';
import { useMarkerStore } from '@/stores/markerStore';
import type { NavigationGuideEndPoint } from '@/types/floor-guide';
import type { RoutePoint } from '@/types/route-simulation';
import { calculateEndPointFromPoi } from '@/utils/map-poi';
import { DESTINATION_MARKER_THEME_FALLBACK, MAP_ICON_URL } from '@/constants/icons';
import { generateNavigationOptions, generateWalkingOptions } from '@/utils/route-simulation';
import { createDestinationPointIconUrl, createWalkingPersonIconUrl } from '@/utils/svg';
import type { DabeeoMap, DabeeoMapData } from 'dabeeomaps';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCalculateIconSize } from './useCalculateIconSize';
import { usePoiDetail } from './usePoiDetail';
import type { IClosestPoiDataOption } from 'dabeeomaps/dist/src/model/IDataOption';
import useTickSound from '../useTickSound';

type ModalState = {
    poiDetail: boolean;
};

const initialModalState: ModalState = {
    poiDetail: false,
};

const getMapPoiDisplayName = (poi: { name?: { lang: string; text: string }[] }, language: string) => {
    if (!Array.isArray(poi?.name) || !poi.name.length) {
        return '';
    }

    return poi.name.find((item) => item.lang === language)?.text || poi.name.find((item) => item.lang === 'ko')?.text || '';
};

export const usePoiContentState = (mapData?: DabeeoMapData, currentMap?: DabeeoMap) => {
    const server = useConfigStore((state) => state.config?.server);
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const language = useConfigStore((state) => state.config?.ui?.language);
    const contrast = useConfigStore((state) => state.config?.ui?.contrast);
    const theme = useConfigStore((state) => state.config?.server?.store?.companyTheme);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const { playTick } = useTickSound();

    const { markerIds, setMarkerIds } = useMarkerStore();

    const { clearMarker } = useMarker(currentMap);

    const { handleGetPoiItemInAllBuildings } = usePoiDetail();
    const { calculateIconSize } = useCalculateIconSize();

    const [modalState, setModalState] = useState<ModalState>(structuredClone(initialModalState));
    const [clickedMapPoiName, setClickedMapPoiName] = useState<string | undefined>(undefined);
    const [endPoint, setEndPoint] = useState<RoutePoint | undefined>(undefined);
    const [routes, setRoutes] = useState<any>(undefined);
    const [selectedRoute, setSelectedRoute] = useState<any | undefined>(undefined);
    const [selectedEndPointId, setSelectedEndPointId] = useState<string | undefined>(undefined);
    const [selectedContentId, setSelectedContentId] = useState<string | undefined>(undefined);
    const [destinationIconUrl, setDestinationIconUrl] = useState(MAP_ICON_URL.DESTINATION_POINT_ICON);
    const [isDestinationIconReady, setIsDestinationIconReady] = useState(false);
    const [walkingIconUrl, setWalkingIconUrl] = useState(MAP_ICON_URL.WALKING_PERSON_ICON);

    const startPoint = useMemo<RoutePoint>(() => {
        return {
            floorId: server?.floor?.floorCode ?? '',
            floorName: server?.floor?.name['ko'] ?? '',
            position: {
                x: server?.machine?.positionX ?? 0,
                y: server?.machine?.positionY ?? 0,
            },
        };
    }, [server?.floor, server?.machine]);

    const currentPoiOptions = useMemo<IClosestPoiDataOption>(() => {
        return {
            floorId: server?.floor?.floorCode ?? '',
            position: {
                x: server?.machine?.positionX ?? 0,
                y: server?.machine?.positionY ?? 0,
            },
            // categoryCode: Array.isArray(poi?.categoryCode) ? poi?.categoryCode[0] : poi?.categoryCode,
        };
    }, [server?.machine?.positionX, server?.machine?.positionY, server?.floor?.floorCode]);

    // endPoint is now direct state, no need for useMemo

    const allPoiContents = useMemo(() => {
        if (!Array.isArray(buildings) || !buildings?.length) {
            return [];
        }

        return buildings.flatMap((building) => (Array.isArray(building.poiContents) ? building.poiContents : []));
    }, [buildings]);

    const poiItemBySelectedEndPointId = useMemo(() => {
        if (selectedContentId) {
            const contentItem = allPoiContents.find((poi) => poi.id === selectedContentId);
            if (contentItem) return contentItem;
        }
        return handleGetPoiItemInAllBuildings(selectedEndPointId);
    }, [selectedEndPointId, selectedContentId, handleGetPoiItemInAllBuildings, allPoiContents]);

    const navigationOptions = useMemo(() => {
        const routeLineColor = contrast ? theme?.route_line_color_hc : theme?.route_line_color;

        return generateNavigationOptions(
            server?.store?.mockRoutingLineWeight,
            calculateIconSize({ width: 26, height: 26 }),
            calculateIconSize({ width: 48, height: 68 }),
            routeLineColor,
            destinationIconUrl
        );
    }, [
        server?.store?.mockRoutingLineWeight,
        calculateIconSize,
        contrast,
        theme?.route_line_color,
        theme?.route_line_color_hc,
        destinationIconUrl,
    ]);

    useEffect(() => {
        let cancelled = false;
        const markerColor = contrast
            ? theme?.route_marker_color_hc || DESTINATION_MARKER_THEME_FALLBACK.CONTRAST
            : theme?.route_marker_color || DESTINATION_MARKER_THEME_FALLBACK.DEFAULT;

        setIsDestinationIconReady(false);
        Promise.all([createDestinationPointIconUrl(markerColor), createWalkingPersonIconUrl(markerColor)]).then(
            ([destinationUrl, walkingUrl]) => {
            if (cancelled) {
                return;
            }

            setDestinationIconUrl(destinationUrl);
            setWalkingIconUrl(walkingUrl);
            setIsDestinationIconReady(true);
            }
        );

        return () => {
            cancelled = true;
        };
    }, [contrast, theme?.route_marker_color, theme?.route_marker_color_hc]);

    const generateEndpointFloorName = useMemo(() => {
        return endPoint?.floorName ?? '';
    }, [endPoint?.floorName]);

    const generateNavigationGuideEndPoint = useMemo<NavigationGuideEndPoint>(() => {
        const selectedPoiContent = selectedContentId
            ? allPoiContents.find((poi) => poi.id === selectedContentId) ?? allPoiContents.find((poi) => poi.studioPoiId === selectedEndPointId)
            : allPoiContents.find((poi) => poi.studioPoiId === selectedEndPointId);

        const selectedBuilding =
            Array.isArray(buildings) && buildings?.length > 0
                ? buildings?.find(
                      (building) =>
                          (building?.buildingCode === selectedPoiContent?.contents?.cstrTownCd?.['ko'] &&
                              building?.storeCode === selectedPoiContent?.contents?.cstrCd?.['ko']) ||
                          selectedPoiContent?.studioBuildingId === building?.studioBuildingId
                  )
                : null;

        const selectedPoiName = selectedPoiContent?.name?.[language] || selectedPoiContent?.name?.['ko'] || '';

        return {
            floorName: generateEndpointFloorName,
            buildingName: selectedBuilding ? (selectedBuilding?.name?.[language] || selectedBuilding?.name?.['ko'] || '') : '',
            poiName: selectedPoiName,
        };
    }, [generateEndpointFloorName, language, buildings, allPoiContents, selectedEndPointId, selectedContentId]);

    const walkingOptions = useMemo(() => {
        const zoom = currentMap?.context?.getMapOptions?.()?.controlOption?.zoom;
        return generateWalkingOptions(
            server?.store?.mockRoutingOption,
            server?.store?.mockRoutingSpeed,
            zoom,
            calculateIconSize({ width: 66, height: 66 }),
            walkingIconUrl
        );
    }, [server?.store?.mockRoutingSpeed, server?.store?.mockRoutingOption, currentMap, calculateIconSize, walkingIconUrl]);

    const handleSetModalState = useCallback((modal: Partial<ModalState>) => {
        setModalState((prev) => ({
            ...prev,
            ...modal,
        }));
    }, []);

    /**
     * Set endpoint from POI - works for both async and immediate use cases
     * Since endPoint is now direct state, no timing issues!
     *
     * @param poi - POI object to convert to endpoint
     */
    const setEndPointFromPoi = useCallback(
        (poi: IPoi | any): void => {
            const calculatedEndPoint = calculateEndPointFromPoi(poi, mapData);
            setEndPoint(calculatedEndPoint);
            setSelectedEndPointId(poi?.id);
        },
        [mapData]
    );

    const resolvePoiContentForStudioPoi = useCallback(
        (studioPoiId: string) => {
            if (selectedContentId) {
                const fromContentId = allPoiContents.find((poi) => poi.id === selectedContentId && poi.studioPoiId === studioPoiId);
                if (fromContentId) {
                    return fromContentId;
                }
            }
            return handleGetPoiItemInAllBuildings(studioPoiId);
        },
        [allPoiContents, selectedContentId, handleGetPoiItemInAllBuildings]
    );

    const handlePoiClick = useCallback(
        (data: any) => {
            if (!Array.isArray(data) || !data?.length || !data[0]?.id) {
                return;
            }

            playTick('ting');

            const studioPoiId = data[0].id as string;
            const poiContent = resolvePoiContentForStudioPoi(studioPoiId);

            if (selectedContentId) {
                const selected = allPoiContents.find((poi) => poi.id === selectedContentId);
                if (selected && selected.studioPoiId !== studioPoiId) {
                    setSelectedContentId(undefined);
                }
            }

            if (poiContent) {
                TrackingService.trackPoi(poiContent.id ?? studioPoiId, undefined, poiContent);
            }

            const poiList = Array.isArray(markerIds?.poiList) ? markerIds?.poiList : [];
            const selectedPoiMarkerId = markerIds?.selectedPoi ? [markerIds?.selectedPoi] : [];
            clearMarker([...poiList, ...selectedPoiMarkerId]);
            setMarkerIds({ selectedPoi: undefined, poiList: undefined });

            setEndPointFromPoi(data[0]);
            setClickedMapPoiName(getMapPoiDisplayName(data[0], language));
            handleSetModalState({ poiDetail: true });
        },
        [
            handleSetModalState,
            clearMarker,
            setMarkerIds,
            markerIds,
            setEndPointFromPoi,
            language,
            playTick,
            resolvePoiContentForStudioPoi,
            selectedContentId,
            allPoiContents,
            setSelectedContentId,
        ]
    );

    const handleResetModalState = useCallback(() => {
        setModalState(structuredClone(initialModalState));
    }, []);

    const handleResetState = useCallback(() => {
        setModalState(structuredClone(initialModalState));
        setClickedMapPoiName(undefined);
        setEndPoint(undefined);
        setRoutes(undefined);
        setSelectedRoute(undefined);
        setSelectedEndPointId(undefined);
        setSelectedContentId(undefined);
    }, []);

    useEffect(() => {
        return () => {
            handleResetState();
        };
    }, [handleResetState]);

    useEffect(() => {
        handleResetState();
    }, [screenMode, handleResetState]);

    return {
        modalState,
        startPoint,
        endPoint,
        navigationOptions,
        walkingOptions,
        routes,
        selectedRoute,
        selectedEndPointId,
        selectedContentId,
        poiItemBySelectedEndPointId,
        generateEndpointFloorName,
        generateNavigationGuideEndPoint,
        currentPoiOptions,
        destinationIconUrl,
        isDestinationIconReady,
        setRoutes,
        setSelectedRoute,
        handleSetModalState,
        handleResetModalState,
        handlePoiClick,
        clickedMapPoiName,
        setSelectedEndPointId,
        setSelectedContentId,
        setEndPoint,
        setEndPointFromPoi, // Unified: works for both async and immediate use cases
    };
};
